import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Loader2, Building2, CreditCard, Wallet, DollarSign, Archive, Eye, Briefcase, MoreHorizontal } from 'lucide-react'
import { HexColorPicker } from 'react-colorful'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from '@/components/ui/button'
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useAccountsStore } from '@/store/accountsStore'
import { useAuthStore } from '@/store/authStore'
import { useCurrenciesStore } from '@/store/currenciesStore'
import { Account } from '@/types/base'
import { toast } from "sonner"
import { cn } from '@/lib/utils'
import * as z from 'zod'
import { Checkbox } from '@/components/ui/checkbox'

const accountTypes = [
  { id: 'checking', icon: Building2, color: '#2962FF' },
  { id: 'savings', icon: Building2, color: '#2962FF' },
  { id: 'credit', icon: CreditCard, color: '#2962FF' },
  { id: 'cash', icon: Wallet, color: '#2962FF' },
  { id: 'investment', icon: Briefcase, color: '#2962FF' },
  { id: 'loan', icon: Building2, color: '#2962FF' },
  { id: 'other', icon: MoreHorizontal, color: '#2962FF' },
] as const

const formSchema = z.object({
  name: z.string().min(1, 'Account name is required'),
  type: z.enum(['checking', 'savings', 'credit', 'cash', 'investment', 'loan', 'other', 'bank']),
  balance: z.coerce.number(),
  currency: z.string().min(1, 'Currency is required'),
  limit: z.coerce.number().optional(),
  notes: z.string().optional(),
  isDefault: z.boolean().default(false),
  isArchived: z.boolean().optional(),
  color: z.string().default('#2962FF'),
  icon: z.string().default('wallet'),
})

type FormData = z.infer<typeof formSchema>

interface EditAccountProps {
  account: Account
  onClose: () => void
  open: boolean
}

export default function EditAccount({ account, onClose, open }: EditAccountProps) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedColor, setSelectedColor] = useState(account.color || '#2962FF')
  const [showColorPicker, setShowColorPicker] = useState(false)
  const { user } = useAuthStore()
  const { updateAccount, fetchAccounts } = useAccountsStore()
  const { currencies } = useCurrenciesStore()

  // Map 'bank' type to 'checking' for backward compatibility
  const displayType = account.type === 'bank' ? 'checking' : account.type;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: account.name,
      type: displayType,
      balance: account.balance,
      currency: account.currency,
      limit: account.limit || undefined,
      notes: account.notes || '',
      isDefault: account.isDefault || false,
      isArchived: account.isArchived || false,
      color: account.color || '#2962FF',
      icon: account.icon || 'wallet',
    },
  })

  const selectedType = form.watch('type')

  const onSubmit = async (data: FormData) => {
    // Check for validation errors
    if (Object.keys(form.formState.errors).length > 0) {
      toast.error(t('accounts.messages.validationError'));
      return;
    }
    
    if (!user || !user.id) {
      toast.error(t('accounts.messages.mustBeLoggedIn'));
      return;
    }
    
    setIsSubmitting(true)
    try {
      const currency = currencies.find(c => c.code === data.currency);
      
      if (!currency) {
        throw new Error('Currency not found');
      }

      // Create the account update object with all required fields
      const accountUpdate = {
        name: data.name,
        type: data.type,
        balance: data.balance,
        currency: data.currency,
        color: selectedColor || '#2962FF',
        icon: account.icon || 'wallet',
        isDefault: data.isDefault,
        notes: data.notes || undefined,
        limit: data.limit || undefined,
        updated_at: new Date().toISOString()
      }
      
      // Check if any required fields are missing
      const requiredFields = ['name', 'type', 'balance', 'currency', 'color', 'icon'];
      const missingFields = requiredFields.filter(field => {
        const value = accountUpdate[field as keyof typeof accountUpdate];
        return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
      });
      
      if (missingFields.length > 0) {
        toast.error(t('accounts.messages.missingFields'));
        return;
      }

      // Call updateAccount with the account ID and the update object
      await updateAccount(account.id, accountUpdate);
      
      // Force refresh accounts data
      if (user.id) {
        await fetchAccounts(user.id);
      }

      toast.success(t('accounts.messages.updateSuccessDescription', { name: account.name }));
      
      // Force close the dialog
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      toast.error(t('accounts.messages.updateError'));
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        onClose();
      }
    }}>
      <DialogContent className="block sm:max-w-[420px] w-full mx-auto p-0 sm:rounded-2xl overflow-hidden bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-0 sm:border sm:border-border sm:dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 shadow-lg">
        <div className="relative">
          <div className="p-5">
            <DialogHeader className="space-y-2 mb-5">
              <DialogTitle className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                {t('accounts.editAccount')}
              </DialogTitle>
              <DialogDescription className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {t('accounts.editAccountDescription')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Account Type */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {t('accounts.fields.type')}
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {accountTypes.map(({ id, icon: Icon }) => (
                    <Button
                      key={id}
                      type="button"
                      variant={selectedType === id ? 'default' : 'outline'}
                      className={cn(
                        "flex flex-col items-center justify-center py-2.5 h-auto space-y-1.5 transition-all duration-200",
                        selectedType === id 
                          ? "bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white shadow-sm" 
                          : "bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark hover:border-secondary dark:hover:border-primary-dark text-text-primary dark:text-text-dark-primary hover:bg-border/5 dark:hover:bg-border-dark/5"
                      )}
                      onClick={() => form.setValue('type', id as 'bank' | 'credit' | 'cash')}
                    >
                      <Icon className={cn(
                        "w-5 h-5 transition-colors",
                        selectedType === id 
                          ? "text-white" 
                          : "text-secondary dark:text-primary-dark"
                      )} />
                      <span className="text-xs font-medium">{t(`accounts.types.${id}`)}</span>
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Account Name */}
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="name" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.fields.name')}
                  </Label>
                  <Input
                    id="name"
                    {...form.register('name')}
                    className="h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg"
                    placeholder={t('accounts.placeholders.name')}
                  />
                  {form.formState.errors.name && (
                    <p className="text-xs text-error mt-1">{form.formState.errors.name.message}</p>
                  )}
                </div>

                {/* Balance */}
                <div className="space-y-1.5">
                  <Label htmlFor="balance" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.fields.balance')}
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary dark:text-primary-dark" />
                    <Input
                      id="balance"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      {...form.register('balance')}
                      className="pl-8 h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Currency */}
                <div className="space-y-1.5">
                  <Label htmlFor="currency" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.fields.currency')}
                  </Label>
                  <Select
                    value={form.watch('currency')}
                    onValueChange={(value) => form.setValue('currency', value)}
                  >
                    <SelectTrigger className="h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg">
                      <SelectValue placeholder={t('accounts.placeholders.currency')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[180px] overflow-y-auto bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
                      {currencies.map((currency) => (
                        <SelectItem 
                          key={currency.code} 
                          value={currency.code}
                          className="focus:bg-secondary/10 dark:focus:bg-primary-dark/10 focus:text-secondary dark:focus:text-primary-dark"
                        >
                          {currency.code} - {currency.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Credit Limit - Only for credit type */}
                {selectedType === 'credit' && (
                  <div className="col-span-2 space-y-1.5">
                    <Label htmlFor="limit" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      {t('accounts.fields.creditLimit')}
                    </Label>
                    <div className="relative">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary dark:text-primary-dark" />
                      <Input
                        id="limit"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        {...form.register('limit')}
                        className="pl-8 h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                )}

                {/* Account Color */}
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.fields.color')}
                  </Label>
                  <Popover open={showColorPicker} onOpenChange={setShowColorPicker}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark hover:border-secondary dark:hover:border-primary-dark hover:bg-border/5 dark:hover:bg-border-dark/5 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded-full ring-1 ring-border dark:ring-border-dark"
                            style={{ backgroundColor: selectedColor || '#2962FF' }}
                          />
                          <span className="text-sm text-text-primary dark:text-text-dark-primary">
                            {selectedColor || '#2962FF'}
                          </span>
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3 bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
                      <HexColorPicker color={selectedColor || '#2962FF'} onChange={setSelectedColor} />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Default Account */}
                <div className="col-span-2 flex items-center space-x-2">
                  <Checkbox
                    id="isDefault"
                    checked={form.watch('isDefault')}
                    onCheckedChange={(checked) => form.setValue('isDefault', checked as boolean)}
                    className="h-4 w-4 rounded border-border dark:border-border-dark data-[state=checked]:bg-secondary data-[state=checked]:border-secondary dark:data-[state=checked]:bg-primary-dark dark:data-[state=checked]:border-primary-dark"
                  />
                  <Label
                    htmlFor="isDefault"
                    className="text-sm text-text-primary dark:text-text-dark-primary cursor-pointer select-none"
                  >
                    {t('accounts.fields.isDefault')}
                  </Label>
                </div>

                {/* Archive Account */}
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="isArchived" 
                    checked={form.watch('isArchived')}
                    onCheckedChange={(checked) => form.setValue('isArchived', checked as boolean)}
                    className="data-[state=checked]:bg-text-secondary data-[state=checked]:border-text-secondary dark:data-[state=checked]:bg-text-dark-secondary dark:data-[state=checked]:border-text-dark-secondary"
                  />
                  <div className="flex items-center space-x-2">
                    {form.watch('isArchived') ? (
                      <Eye className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                    ) : (
                      <Archive className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                    )}
                    <Label 
                      htmlFor="isArchived" 
                      className="text-sm font-medium text-text-primary dark:text-text-dark-primary cursor-pointer"
                    >
                      {form.watch('isArchived') ? t('common.unarchive') : t('common.archive')}
                    </Label>
                  </div>
                </div>

                {/* Notes */}
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="notes" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {t('accounts.fields.notes')}
                  </Label>
                  <Input
                    id="notes"
                    {...form.register('notes')}
                    className="h-10 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-1 focus:ring-secondary dark:focus:ring-primary-dark rounded-lg"
                    placeholder={t('accounts.placeholders.notes')}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="w-full sm:w-1/2 h-10 bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 hover:text-secondary dark:hover:text-primary-dark border-secondary dark:border-primary-dark backdrop-blur-xl backdrop-saturate-150 rounded-lg"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-1/2 h-10 bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white shadow-sm transition-all duration-200 rounded-lg"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('actions.save')
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}